import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Avatar, Box, Checkbox, Grid } from "@mui/material";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import CalendarIcon from "../assets/Icons/CalendarIcon";
import ProposalState from "../constants/ProposalState";
import UserType from "../constants/UserType";
import { fetchGigProposal } from "../firebase/gig";
import {
  addFavouriteGig,
  fetchUserProfileImage,
  removeFavouriteGig,
} from "../firebase/user";
import {
  convertDateFormat,
  getGigEndDate,
  getGigStartDate,
} from "../helper/dates";
import { getStateData } from "../helper/gigStates";
import { Gig } from "../interfaces/Gig";
import { theme } from "../mui-customs/Theme";
import { RootState } from "../redux/store";
import Button from "./Button";
import DataChip from "./gigs/DataChip";
import GigTimingsTitle from "./gigs/GigTimingsTitle";
import GigStateTag from "./GigStateTag";
import Typography from "./Typography";

interface GigCardProps {
  gig: Partial<Gig>;
  gigStatusDisplay?: boolean;
  gigState?: ProposalState;
}

const GigCard = ({ gig, gigStatusDisplay = false, gigState }: GigCardProps) => {
  const gigStateValue = gig && (gigState ? gigState : gig.gigState);
  const [favourite, setFavourite] = useState(false);
  const { uid: userUid } = useSelector((state: RootState) => state.user.user);
  const userType = useSelector((state: RootState) => state.user.user.userType);

  const handleClick = async () => {
    if (userUid) {
      if (userType === UserType.Chef && gig.gigId) {
        const proposalData = await fetchGigProposal(userUid, gig.gigId);
        if (!proposalData.proposalState) {
          router.push(`/gig/${gig.gigId}/proposal/${userUid}`);
        }
        if (
          !proposalData.proposalState ||
          proposalData.proposalState === ProposalState.DRAFT
        ) {
          router.push(`/gig/${gig.gigId}/proposal/${userUid}`);
        } else {
          router.push(`/gigs/${userUid}/proposal-preview/${gig.gigId}`);
        }
      } else {
        if (gig.ownerUid === userUid) {
          router.push(`/gigs/${userUid}/all-proposal/${gig.gigId}`);
        } else {
          router.push(`/gig/${gig.gigId}/proposal/${userUid}`);
        }
      }
    } else {
      router.push(`/gig/${gig.gigId}`);
    }
  };
  const [gigOwnerProfileImage, setGigOwnerProfileImage] = useState<string>();
  const setUserProfileImage = async () => {
    if (gig.ownerUid) {
      const profileImage = await fetchUserProfileImage(gig.ownerUid);
      profileImage && setGigOwnerProfileImage(profileImage);
    }
  };

  useEffect(() => {
    let isLiked = false;
    if (gig.likedBy && userUid && gig.likedBy.indexOf(userUid) > -1) {
      isLiked = true;
    }
    setFavourite(isLiked);
    setUserProfileImage();
  }, []);

  const router = useRouter();

  const handleLikeDislike = async (e: React.ChangeEvent) => {
    e.stopPropagation();
    const favouriteData = !favourite;
    setFavourite(favouriteData);
    if (!userUid) {
      return router.push("/auth/login");
    }
    if (gig.gigId) {
      if (favouriteData === true) {
        await addFavouriteGig(userUid, gig.gigId);
      } else {
        await removeFavouriteGig(userUid, gig.gigId);
      }
    }
  };
  const startDate = getGigStartDate(gig);
  const endDate = getGigEndDate(gig);

  const titleList = [
    {
      title: "Gig Created",
      date: gig?.createdAt ? convertDateFormat(gig?.createdAt) : " N/A",
    },
    {
      title: "Gig Start Date",
      date: startDate ? convertDateFormat(startDate) : "N/A",
    },
    {
      title: "Gig End Date",
      date: endDate ? convertDateFormat(endDate) : "N/A",
    },
  ];

  return (
    <Box py={{ xs: 1 }}>
      <Box
        onClick={handleClick}
        sx={{
          p: { xs: 3, md: 4 },
          cursor: "pointer",
          borderLeft: `4px solid ${getStateData(gigStateValue).color}`,
        }}
      >
        <Box
          sx={{
            background: `${
              gig && gig.headerImageUrl
                ? `url("${gig.headerImageUrl}")`
                : theme.grey2[100]
            }`,
            height: { xs: "4rem", md: "9.8rem" },
            width: "100%",
            display: "flex",
            alignItems: { xs: "start", md: "end" },
            justifyContent: "space-between",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            borderRadius: "0.375rem",
          }}
        >
          <Box sx={{ padding: 2 }}>
            {gigStateValue && <GigStateTag gigStateValue={gigStateValue} />}
          </Box>
          {gig?.isPrivate && (
            <Box
              sx={{
                alignSelf: "flex-start",
                backgroundColor: "rgba(25, 17, 34, 0.75)",
                color: theme.whiteBg.WBG1,
                borderBottomLeftRadius: "8px",
                px: 1.5,
                py: 0.5,
                fontSize: { xs: "0.65rem", md: "0.75rem" },
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Private gig
            </Box>
          )}
        </Box>
        <Grid container sx={{ py: "1rem" }}>
          <Grid
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "left",
            }}
            item
            xs={10}
          >
            <Avatar
              sx={{
                width: { md: "1.5rem" },
                height: { md: "1.5rem" },
                cursor: "pointer",
              }}
              alt="Profile image"
              // onClick={handleAvatarClick}
              src={gigOwnerProfileImage}
            />
            <Typography
              variant="title5"
              sx={{
                color: `${theme.black[800]} !important`,
                marginLeft: "1em",
              }}
            >
              {gig && gig.title}
            </Typography>
          </Grid>

          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "right",
            }}
            xs={2}
          >
            <Checkbox
              sx={{ ml: "auto" }}
              checked={favourite}
              onChange={handleLikeDislike}
              icon={<FavoriteBorderIcon />}
              checkedIcon={<FavoriteIcon sx={{ color: theme.brand2[300] }} />}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: "flex" }}>
          <Box sx={{ flexGrow: "1" }}>
            <Grid
              sx={{
                mb: "1rem",
                display: "flex",
                alignItems: "flex-end",
                width: "100%",
              }}
              container
              spacing={2}
            >
              <DataChip
                title="Type of Service"
                data={[
                  <Typography
                    variant="small5"
                    sx={{
                      color: theme.brand1[500],
                      maxWidth: { xs: "100px", md: "130px" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    key={gig && gig.serviceType}
                  >
                    {gig && gig.serviceType}
                  </Typography>,
                ]}
              />
              <DataChip
                title="Location"
                data={[
                  <Typography
                    variant="small5"
                    sx={{
                      color: theme.brand1[500],
                      maxWidth: { xs: "60px", md: "80px" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    key={
                      gig && `${gig.location?.locality}, ${gig.location?.state}`
                    }
                  >
                    {gig?.location?.locality &&
                      gig?.location?.state &&
                      `${gig.location?.locality}, ${gig.location?.state}`}
                  </Typography>,
                ]}
              />
              <DataChip
                title="Guests"
                data={[
                  <>
                    {gig && (
                      <Typography
                        variant="small4"
                        sx={{
                          color: theme.brand1[500],
                          maxWidth: { xs: "40px", md: "60px" },
                        }}
                        key={gig && `${gig.numberOfGuests}`}
                      >
                        {gig.numberOfGuests}
                      </Typography>
                    )}
                  </>,
                ]}
              />
              <DataChip
                title="Budget"
                data={[
                  <Typography
                    variant="small4"
                    sx={{
                      color: theme.brand1[500],
                      maxWidth: { xs: "40px", md: "60px" },
                    }}
                    key={gig?.budget}
                  >
                    {`$${gig?.budget}`}
                  </Typography>,
                ]}
              />
            </Grid>
          </Box>

          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Button sx={{ width: { md: "200px" } }} onClick={handleClick}>
              View Details
            </Button>
          </Box>
        </Box>

        <Grid container sx={{ my: "1rem" }}>
          <Grid
            item
            xs={1}
            sx={{
              display: "flex",
              justifyContent: "space-evenly",
              alignItems: "center",
              mr: "1rem",
              flexDirection: "column",
            }}
          >
            <CalendarIcon />
            <Box sx={{ display: { lg: "none" } }}>
              <CalendarIcon />
            </Box>
          </Grid>

          <Grid container item xs={10}>
            <Grid item xs={12} sx={{ my: "1rem" }}>
              <GigTimingsTitle titleList={titleList} />
            </Grid>
          </Grid>
        </Grid>
        {gigStateValue && (
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              "& button": {
                width: "100% !important",
              },
            }}
          >
            <Button sx={{ width: { md: "200px" } }} onClick={handleClick}>
              View Details
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GigCard;
